import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantDto } from "@/features/merchant/api/dto";
import type { MerchantStatus } from "@/features/shared/api/enums";

/**
 * Force le statut d'un marchand
 * (`POST /Merchants/{id}/status?status=...&actingMerchantId=...`). Cibles
 * admises : ACTIVE, SUSPENDED, CLOSED. Rôles : flashpay-superadmin,
 * flashpay-admin (guide §6.6).
 */
export const useSetMerchantStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchants", "set-status"],
    mutationFn: ({
      merchantId,
      status,
      actingMerchantId,
    }: {
      merchantId: string;
      status: MerchantStatus;
      actingMerchantId?: string;
    }) => {
      const params = new URLSearchParams({ status });
      if (actingMerchantId) params.set("actingMerchantId", actingMerchantId);
      return mutateData({
        url: `${merchantEndpoints.merchants.status(merchantId)}?${params.toString()}`,
        method: "POST",
      }) as Promise<MerchantDto>;
    },
    onSuccess: (_data, { merchantId }) => {
      // `mutateData` peut renvoyer un corps vide (enveloppe statusServer
      // sans `id`) : toujours invalider via l'id de la mutation, pas via
      // la réponse. Liste + fiche + sous-ressources de statut.
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(merchantId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetMerchantStatus failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
